import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, DollarSign, MessageSquare, TrendingUp, Clock, CheckCircle, XCircle } from 'lucide-react';
import { getDashboardStats } from '../../utils/adminApi';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#D4AF37] text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Revenue',
      value: `€${stats?.total_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'from-[#D4AF37] to-[#F4C542]',
      bgColor: 'bg-[#D4AF37]/10',
      link: '/admin/orders'
    },
    {
      title: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'from-[#5A6F8F] to-[#6A7F9F]',
      bgColor: 'bg-[#5A6F8F]/10',
      link: '/admin/orders'
    },
    {
      title: 'Pending Orders',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: 'from-[#ff965a] to-[#d1903a]',
      bgColor: 'bg-[#ff965a]/10',
      link: '/admin/orders'
    },
    {
      title: 'Processing',
      value: stats?.processing_orders || 0,
      icon: TrendingUp,
      color: 'from-[#88a2ff] to-[#1f47e6]',
      bgColor: 'bg-[#88a2ff]/10',
      link: '/admin/orders'
    },
    {
      title: 'Completed',
      value: stats?.completed_orders || 0,
      icon: CheckCircle,
      color: 'from-[#78d692] to-[#a1a500]',
      bgColor: 'bg-[#78d692]/10',
      link: '/admin/orders'
    },
    {
      title: 'Unread Messages',
      value: stats?.unread_messages || 0,
      icon: MessageSquare,
      color: 'from-[#ff84e4] to-[#ffd1e7]',
      bgColor: 'bg-[#ff84e4]/10',
      link: '/admin/messages'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard Overview</h1>
        <p className="text-gray-400">Monitor your business performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              onClick={() => navigate(stat.link)}
              className="group p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-105 relative overflow-hidden cursor-pointer"
              data-testid={`dashboard-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/10 group-hover:to-transparent transition-all duration-300"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                <div className="text-3xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                
                <div className="text-sm text-gray-400 uppercase tracking-wide">
                  {stat.title}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDashboard;
